import { cn } from "@/lib/utils";

import HelpTip, { type HelpTipContent } from "./HelpTip";
import { DS_INPUT, DS_TEXTAREA } from "./styles";

/**
 * Campo de formulário (README "Formulários"): rótulo sempre acima (12px,
 * 600), asterisco vermelho no obrigatório, erro embaixo do campo (12px,
 * vermelho de alerta). O erro aparece ao sair do campo — quem valida é o
 * formulário; aqui só se desenha.
 *
 * `help`: o "?" logo depois do rótulo (a dica que antes era um parágrafo ou
 * um "?" próprio da tela) — fora do `<label>`, para o toque nele não focar o
 * campo.
 */
export function Field({
  label,
  htmlFor,
  required = false,
  hint,
  error,
  help,
  children,
  className,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string | null;
  help?: HelpTipContent;
  children: React.ReactNode;
  className?: string;
}) {
  const hintId = htmlFor ? `${htmlFor}-hint` : undefined;
  const errorId = htmlFor ? `${htmlFor}-error` : undefined;
  const labelEl = (
    <label htmlFor={htmlFor} className="text-xs font-semibold text-ink-2">
      {label}
      {required && (
        <span className="ml-0.5 text-alert-ink" aria-hidden>
          *
        </span>
      )}
      {required && <span className="sr-only"> (obrigatório)</span>}
    </label>
  );
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      {help ? (
        <div className="flex items-center gap-0.5">
          {labelEl}
          <HelpTip help={help} className="-my-3 lg:-my-2" />
        </div>
      ) : (
        labelEl
      )}
      {children}
      {hint && !error && (
        <p id={hintId} className="text-xs text-ink-2">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-alert-ink">
          {error}
        </p>
      )}
    </div>
  );
}

/** Campo de texto no padrão (38px, 44px e 16px no celular). */
export function TextInput({
  className,
  invalid,
  ...props
}: React.ComponentProps<"input"> & { invalid?: boolean }) {
  return (
    <input
      aria-invalid={invalid || undefined}
      className={cn(DS_INPUT, className)}
      {...props}
    />
  );
}

/** Área de texto no padrão (mín. 78px). */
export function TextArea({
  className,
  invalid,
  ...props
}: React.ComponentProps<"textarea"> & { invalid?: boolean }) {
  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={cn(DS_TEXTAREA, className)}
      {...props}
    />
  );
}

/**
 * Coluna de propriedades à direita da janela (tela-modelo 1g, tarefa):
 * rótulo de 11px em maiúsculas + valor de 14px.
 */
export function PropsList({
  items,
}: {
  items: { label: string; value: React.ReactNode }[];
}) {
  return (
    <dl className="flex flex-col gap-4">
      {items.map((i) => (
        <div key={i.label} className="flex flex-col gap-1">
          <dt className="text-2xs font-semibold tracking-label text-ink-2 uppercase">
            {i.label}
          </dt>
          <dd className="text-sm font-semibold text-ink">{i.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Propriedade EDITÁVEL da coluna da direita (tela-modelo 1g, tarefa): rótulo
 * de 11px em maiúsculas + o controle, com linha fina entre as propriedades.
 * `hint` é uma linha de apoio embaixo (ex.: "Sem aprovador: conclui direto").
 */
export function PropField({
  label,
  htmlFor,
  required = false,
  hint,
  labelAside,
  children,
  className,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  /** Na ponta direita da linha do rótulo (ex.: o "Salvo" do campo que grava
   *  na hora, na janela da tarefa aberta). */
  labelAside?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-col gap-1.5 border-b border-line py-3 first:pt-0 last:border-b-0 last:pb-0",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <label
          htmlFor={htmlFor}
          className="min-w-0 flex-1 text-2xs font-semibold tracking-label text-ink-2 uppercase"
        >
          {label}
          {required && (
            <span className="ml-0.5 text-alert-ink" aria-hidden>
              *
            </span>
          )}
          {required && <span className="sr-only"> (obrigatório)</span>}
        </label>
        {labelAside}
      </div>
      {children}
      {hint && <p className="text-xs text-ink-2">{hint}</p>}
    </div>
  );
}

/** Título de seção dentro de formulário (régua de 2px, meta à direita). */
export function FormSection({
  title,
  meta,
  children,
  className,
}: {
  title: string;
  meta?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("flex flex-col gap-3.5", className)}>
      <div className="flex items-baseline gap-2.5 border-b-2 border-ink pb-1.5">
        <h3 className="text-sm font-extrabold tracking-caps text-ink uppercase">
          {title}
        </h3>
        {meta && <span className="text-xs text-ink-2">{meta}</span>}
      </div>
      {children}
    </section>
  );
}
